import { FC, useEffect, useId, useMemo, useState } from 'react';
import {
  AutomaticPromotion,
  fetchAutomaticPromotion,
} from '../api/automaticPromotions';

interface PromotionDetailsModalProps {
  open: boolean;
  promotionId: string | null;
  onClose: () => void;
}

const formatBoolean = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) {
    return '—';
  }
  return value ? 'Yes' : 'No';
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleString();
};

const PromotionDetailsModal: FC<PromotionDetailsModalProps> = ({
  open,
  promotionId,
  onClose,
}) => {
  const titleId = useId();
  const [data, setData] = useState<AutomaticPromotion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !promotionId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    fetchAutomaticPromotion(promotionId)
      .then((response) => {
        if (!active) return;
        setData(response);
      })
      .catch((err) => {
        if (!active) return;
        const message =
          err instanceof Error ? err.message : 'Failed to load promotion.';
        setError(message);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, promotionId]);

  const promotionType = useMemo(() => {
    if (!data) {
      return '—';
    }
    return data.promotionTypeKey ?? data.promotionType ?? '—';
  }, [data]);

  const locationNames = useMemo(() => {
    if (!data?.locations || data.locations.length === 0) {
      return ['No linked locations'];
    }
    return data.locations.map((item) => item?.name ?? `Location ${item?.id ?? ''}`);
  }, [data?.locations]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="flex w-full max-h-[90vh] max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl">
        <div className="flex w-full items-center border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex-1">
            <h2 id={titleId} className="text-lg font-semibold text-slate-900">
              {data?.name ?? 'Promotion details'}
            </h2>
            {data?.erpId ? (
              <p className="mt-0.5 text-xs text-slate-500">ERP ID {data.erpId}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 text-sm text-slate-700">
          {loading ? (
            <p className="text-slate-500">Loading promotion…</p>
          ) : error ? (
            <p className="text-danger">{error}</p>
          ) : !data ? (
            <p className="text-slate-500">Promotion not found.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-[260px_1fr]">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <h3 className="text-base font-semibold text-slate-700">
                    Promotion Details
                  </h3>
                  <dl className="mt-3 space-y-2 text-sm">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Type
                      </dt>
                      <dd>{promotionType}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Timeframe
                      </dt>
                      <dd>{data.timeframeType ?? '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        POS Available
                      </dt>
                      <dd>{formatBoolean(data.isAvailableInPos)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Membership Required
                      </dt>
                      <dd>{formatBoolean(data.isMembershipRequired)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Automatic
                      </dt>
                      <dd>{formatBoolean(data.isAutomatic)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Dream Price Applicable
                      </dt>
                      <dd>{formatBoolean(data.dreamPriceApplicable)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Updated
                      </dt>
                      <dd>{formatDateTime(data.updatedAt)}</dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <h3 className="text-base font-semibold text-slate-700">
                    Eligibility & Schedule
                  </h3>
                  <dl className="mt-3 space-y-2 text-sm">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Walk-in members
                      </dt>
                      <dd>{formatBoolean(data.allowWalkInMember)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Registered members
                      </dt>
                      <dd>{formatBoolean(data.allowRegisteredMember)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Employees
                      </dt>
                      <dd>{formatBoolean(data.allowEmployee)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Start date
                      </dt>
                      <dd>{formatDateTime(data.startDate)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        End date
                      </dt>
                      <dd>{formatDateTime(data.endDate)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Start time
                      </dt>
                      <dd>{data.startTime ?? '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        End time
                      </dt>
                      <dd>{data.endTime ?? '—'}</dd>
                    </div>
                  </dl>

                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-slate-700">
                      Locations
                    </h4>
                    <ul className="mt-2 space-y-1 text-xs text-slate-600">
                      {locationNames.map((name, index) => (
                        <li
                          key={`${name}-${index}`}
                          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5"
                        >
                          {name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {Array.isArray(data.promotionTiers) && data.promotionTiers.length > 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <h3 className="text-base font-semibold text-slate-700">
                    Discount tiers
                  </h3>
                  <div className="mt-3 space-y-3 text-xs text-slate-700">
                    {data.promotionTiers.map((tier, index) => {
                      const knownKeys = new Set([
                        'minimum_spend_amount',
                        'flat_amount',
                        'percentage',
                      ]);
                      const otherEntries = Object.entries(
                        tier as Record<string, unknown>,
                      ).filter(([key, value]) => {
                        if (knownKeys.has(key)) return false;
                        if (value === null || value === undefined) return false;
                        if (value === '') return false;
                        return true;
                      });

                      const formatKey = (key: string) =>
                        key
                          .replace(/_/g, ' ')
                          .replace(/\s+/g, ' ')
                          .replace(/^\w/, (c) => c.toUpperCase());

                      const entries: { label: string; value: unknown }[] = [];
                      if (tier.minimum_spend_amount !== null && tier.minimum_spend_amount !== undefined) {
                        entries.push({
                          label: 'Min spend',
                          value: tier.minimum_spend_amount,
                        });
                      }
                      if (tier.flat_amount !== null && tier.flat_amount !== undefined) {
                        entries.push({
                          label: 'Flat amount',
                          value: tier.flat_amount,
                        });
                      }
                      if (tier.percentage !== null && tier.percentage !== undefined) {
                        entries.push({
                          label: 'Percentage',
                          value: tier.percentage,
                        });
                      }

                      otherEntries.forEach(([key, value]) => {
                        entries.push({
                          label: formatKey(key),
                          value,
                        });
                      });

                      return (
                        <div
                          key={index}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2"
                        >
                          <p className="text-xs font-semibold text-slate-700">
                            Tier {index + 1}
                          </p>
                          <dl className="mt-1 space-y-1">
                            {entries.map((entry) => (
                              <div className="flex gap-2" key={entry.label}>
                                <dt className="w-32 text-[11px] font-semibold text-slate-600">
                                  {entry.label}
                                </dt>
                                <dd className="text-slate-700">
                                  {typeof entry.value === 'object'
                                    ? JSON.stringify(entry.value)
                                    : String(entry.value)}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-3">
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary/20"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromotionDetailsModal;
